// @ts-nocheck
/**
 * chat-stream.js — SSE streaming client for the AI Florist chat.
 *
 * Uses POST /chat/stream to receive real-time events as Fleur processes
 * the message (typing → designing → message). Falls back to the regular
 * synchronous POST /chat (via AIFlorist.fetch XHR wrapper) on failure.
 *
 * Shopify's analytics script replaces window.fetch, which breaks
 * ReadableStream access. We grab the browser's native fetch from a
 * hidden iframe — a standard technique that avoids the interception.
 *
 * Exposes: window.AIFlorist.stream.send(url, payload, handlers)
 *
 * Load order: after chat-fetch.js, before chat.js
 */
(function () {
  'use strict';

  window.AIFlorist = window.AIFlorist || {};
  var debug = window.AIFlorist.debug || { log: function () {}, warn: function () {}, error: function () {} };

  // ── Grab native fetch (bypasses Shopify's wrapper) ──────────────────

  var nativeFetch = null;

  /**
   * Lazily obtain the browser's native fetch via a hidden iframe.
   * Shopify's analytics replaces window.fetch with a wrapper that strips
   * ReadableStream access. A hidden iframe gives us the real API.
   *
   * The iframe MUST stay in the DOM — removing it destroys the browsing
   * context and makes the captured fetch throw immediately.
   */
  function getNativeFetch() {
    if (nativeFetch) return nativeFetch;

    try {
      var iframe = document.createElement('iframe');
      iframe.style.cssText = 'display:none!important;width:0;height:0;border:0;position:absolute;pointer-events:none';
      iframe.setAttribute('aria-hidden', 'true');
      iframe.tabIndex = -1;
      document.body.appendChild(iframe);
      nativeFetch = iframe.contentWindow.fetch.bind(iframe.contentWindow);
    } catch (e) {
      debug.warn('[AI Florist] Could not obtain native fetch, using window.fetch');
      nativeFetch = window.fetch.bind(window);
    }

    return nativeFetch;
  }

  // ── SSE line parser ─────────────────────────────────────────────────

  /**
   * Parse SSE text chunk into individual events.
   *
   * SSE format: `data: {"type":"typing"}\n\n`
   * A chunk may contain multiple events or partial lines.
   *
   * @param {string} chunk - Raw text from the stream
   * @param {string} buffer - Leftover text from the previous chunk
   * @returns {{ events: object[], remaining: string }}
   */
  function parseSSE(chunk, buffer) {
    var text = buffer + chunk;
    var events = [];
    var lines = text.split('\n');

    // The last element may be a partial line — keep it as the new buffer
    var remaining = lines.pop() || '';

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      if (!line) continue; // blank line = event separator

      if (line.indexOf('data: ') === 0) {
        var json = line.substring(6);
        try {
          events.push(JSON.parse(json));
        } catch (e) {
          debug.warn('[AI Florist] Failed to parse SSE event:', json);
        }
      }
    }

    return { events: events, remaining: remaining };
  }

  function classifyStreamFailure(err) {
    var msg = String((err && err.message) || '');
    if (msg.indexOf('Stream request failed:') === 0) return 'http_error';
    if (msg.indexOf('No response body') === 0) return 'no_stream_body';
    if (msg.indexOf('Failed to fetch') !== -1) return 'network_error';
    if (msg.indexOf('NetworkError') !== -1) return 'network_error';
    return 'unknown_error';
  }

  // ── Stream sender ───────────────────────────────────────────────────

  /**
   * Send a chat message via SSE streaming (POST /chat/stream).
   *
   * @param {string} baseUrl - Worker base URL (e.g. https://worker.workers.dev)
   * @param {object} payload - { conversationId, message, customer?, image? }
   * @param {object} handlers - Event callbacks
   * @param {function} handlers.onAck        - (conversationId: string) => void
   * @param {function} handlers.onTyping     - () => void
   * @param {function} handlers.onToolStart  - (toolName: string) => void
   * @param {function} handlers.onDesigning  - () => void
   * @param {function} handlers.onToolComplete - (toolName: string) => void
   * @param {function} handlers.onMessage    - (content, metadata, conversationId, state) => void
   * @param {function} handlers.onState      - (state: string) => void
   * @param {function} handlers.onError      - (message: string) => void
   * @param {function} handlers.onDone       - () => void
   * @param {AbortSignal} [signal] - Optional abort signal
   * @returns {Promise<void>}
   */
  async function send(baseUrl, payload, handlers, signal, options) {
    var browserFetch = getNativeFetch();
    var uiOptions = options && options.uiOptions ? options.uiOptions : null;
    var allowSyncFallback = !options || options.allowSyncFallback !== false;

    try {
      var response = await browserFetch(baseUrl + '/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: signal,
      });

      if (!response.ok) {
        throw new Error('Stream request failed: ' + response.status);
      }

      if (!response.body) {
        throw new Error('No response body — streaming not supported');
      }

      // Read the stream
      var reader = response.body.getReader();
      var decoder = new TextDecoder();
      var buffer = '';
      var done = false;

      while (!done) {
        var result = await reader.read();
        done = result.done;

        if (result.value) {
          var chunk = decoder.decode(result.value, { stream: !done });
          var parsed = parseSSE(chunk, buffer);
          buffer = parsed.remaining;

          for (var i = 0; i < parsed.events.length; i++) {
            dispatchEvent(parsed.events[i], handlers);
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        var final = parseSSE(buffer + '\n', '');
        for (var j = 0; j < final.events.length; j++) {
          dispatchEvent(final.events[j], handlers);
        }
      }
    } catch (err) {
      // If it's an abort, rethrow so the caller can handle it
      if (err.name === 'AbortError') throw err;
      var reason = classifyStreamFailure(err);

      if (!allowSyncFallback) {
        debug.warn('[AI Florist] SSE stream failed with fallback disabled', reason);
        if (handlers.onError) handlers.onError('realtime_unavailable');
        if (handlers.onDone) handlers.onDone();
        return;
      }

      debug.warn('[AI Florist] SSE stream failed, falling back to sync', reason);

      // ── Fallback to POST /chat ──────────────────────────────────────
      await fallbackSync(baseUrl, payload, handlers, signal, uiOptions);
    }
  }

  // ── Event dispatcher ────────────────────────────────────────────────

  function dispatchEvent(event, handlers) {
    if (!event || !event.type) return;

    switch (event.type) {
      case 'ack':
        if (handlers.onAck) handlers.onAck(event.conversationId);
        break;
      case 'typing':
        if (handlers.onTyping) handlers.onTyping();
        break;
      case 'tool_start':
        if (handlers.onToolStart) handlers.onToolStart(event.tool);
        break;
      case 'designing':
        if (handlers.onDesigning) handlers.onDesigning();
        break;
      case 'tool_complete':
        if (handlers.onToolComplete) handlers.onToolComplete(event.tool);
        break;
      case 'message':
        if (handlers.onMessage) {
          handlers.onMessage(event.content, event.metadata, event.conversationId, event.state);
        }
        break;
      case 'state':
        if (handlers.onState) handlers.onState(event.state);
        break;
      case 'error':
        if (handlers.onError) handlers.onError(event.message);
        break;
      case 'done':
        if (handlers.onDone) handlers.onDone();
        break;
      default:
        debug.warn('[AI Florist] Unknown stream event:', event.type);
    }
  }

  // ── Sync fallback (POST /chat via XHR) ──────────────────────────────

  /**
   * Fallback: use the synchronous POST /chat endpoint if streaming fails.
   * Synthesises the same event callbacks so the caller doesn't know the difference.
   *
   * Default fallback stays on typing dots. For explicit regenerate-intent sends,
   * allow a delayed designing indicator since sync mode has no live SSE events.
   */
  async function fallbackSync(baseUrl, payload, handlers, signal, uiOptions) {
    if (handlers.onTyping) handlers.onTyping();
    var designingTimer = null;
    // Text-only responses arrive in 2-5s (timer cleared before it fires).
    // Image generation takes 25-60s, so the shimmer improves perceived wait.
    var shimmerDelay = (uiOptions && uiOptions.expectGeneration === true) ? 4000 : 8000;
    designingTimer = setTimeout(function () {
      if (handlers.onDesigning) handlers.onDesigning();
    }, shimmerDelay);

    try {
      var res = await window.AIFlorist.fetch(baseUrl + '/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: signal,
      });

      if (!res.ok) throw new Error('Request failed: ' + res.status);

      var data = await res.json();
      if (designingTimer) clearTimeout(designingTimer);

      if (handlers.onAck) handlers.onAck(data.conversationId);
      if (handlers.onMessage) {
        handlers.onMessage(
          data.message.content,
          data.message.metadata,
          data.conversationId,
          data.state
        );
      }
      if (handlers.onDone) handlers.onDone();
    } catch (err) {
      if (designingTimer) clearTimeout(designingTimer);
      if (err.name === 'AbortError') throw err;

      if (handlers.onError) {
        handlers.onError('Something went wrong. Please try again.');
      }
      if (handlers.onDone) handlers.onDone();
    }
  }

  // ── Public API ──────────────────────────────────────────────────────

  window.AIFlorist.stream = {
    send: send,
  };
})();
